import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import myAxios from '../AxiosUrl';
import styles from './AICalling.module.css';

/**
 * AI Calling → the agent's settings, one section per tab (all saved together, PUT call/agent):
 *  section "shop"    — shop name, agent name, voice, shop info (used by every AI call)
 *  section "support" — who answers calls to the shop's number: off / AI / softphone / AI then a
 *                      person; ring time; the support AI's instructions; test call
 *  section "orders"  — automatic order confirmation (on/off, delay, hours), its instructions,
 *                      call an order now, test call
 */

const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const INFO_MAX = 3000;
const INFO_EXAMPLE = 'Delivery charge: ৳70 inside Dhaka, ৳130 outside Dhaka.\nDelivery time: 1–2 days inside Dhaka, 2–4 days outside.\nPayment: cash on delivery.\nExchange: within 3 days if the size does not fit; the customer pays the delivery charge.';
const SUPPORT_EXAMPLE = 'Greet warmly. If they ask for a discount, say only the offers on the website apply.\nFor a wrong or damaged product, say sorry and take the order number with a message for us.';
const ORDER_EXAMPLE = 'For orders outside Dhaka, ask for ৳150 advance by bKash before we ship (tell them we will send the bKash number by SMS).\nIf the order total is above ৳3000, also confirm their alternative phone number.';

const MODES = [
    ['ai', 'AI agent', 'The AI answers every call: order status, delivery and return questions, takes a message for the rest.'],
    ['ai_then_human', 'AI first, then a person', 'The AI answers. If the caller wants a person, it rings your softphone; nobody picks up → the AI takes a message.'],
    ['softphone', 'Softphone (people)', 'Calls ring your softphone (the phone button at the bottom right of every page). Nobody online → a missed call.'],
    ['off', 'Off', 'Calls to your number are not answered.'],
];

function TestCall({ kind, hasNumber, balance, campaignId, label }) {
    const [mobile, setMobile] = useState('');
    const [busy, setBusy] = useState(false);
    const go = async () => {
        if (!/^01[3-9]\d{8}$/.test(mobile.trim())) return toast.error('Enter your mobile number (01XXXXXXXXX)');
        try {
            setBusy(true);
            await myAxios.post('call/test', { mobile: mobile.trim(), kind, campaignId });
            toast.success('Calling your phone now — answer it to hear the AI');
        } catch (err) {
            toast.error(errMsg(err, 'Could not start the call'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className={styles.card}>
            <h2 className={styles.cardTitle}>{label || 'Test call'}</h2>
            <p className={styles.hint} style={{ marginTop: 6 }}>
                The AI calls your phone and talks to you like a customer{kind === 'order' ? ' with a sample order' : kind === 'support' ? ' who called your shop' : ''} (save first). Nothing is changed in your orders.
                It is paid from your call balance like any call.
            </p>
            <div className={styles.row} style={{ marginTop: 10 }}>
                <div style={{ maxWidth: 220, flex: '1 1 180px' }}>
                    <input className={styles.input} inputMode="numeric" placeholder="01XXXXXXXXX" value={mobile} onChange={(e) => setMobile(e.target.value)} aria-label="Your mobile number" />
                </div>
                <button className={styles.btnPrimary} onClick={go} disabled={busy || !hasNumber || !(balance > 0)}>
                    {busy ? 'Calling…' : 'Call me'}
                </button>
            </div>
            {(!hasNumber || !(balance > 0)) && <p className={styles.hint}>{!hasNumber ? 'Needs an active number.' : 'Top up your call balance first.'}</p>}
        </div>
    );
}

export { TestCall };

function CallOrderNow({ hasNumber }) {
    const [invoice, setInvoice] = useState('');
    const [busy, setBusy] = useState(false);
    const go = async () => {
        if (!invoice.trim()) return toast.error('Enter the order (invoice) number');
        try {
            setBusy(true);
            const res = await myAxios.post('call/order', { invoice: invoice.trim() });
            toast.success(res.data?.message || 'Queued');
            setInvoice('');
        } catch (err) {
            toast.error(errMsg(err, 'Could not queue the call'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className={styles.card}>
            <h2 className={styles.cardTitle}>Call an order now</h2>
            <p className={styles.hint} style={{ marginTop: 6 }}>
                The AI calls this order's customer to confirm it — even when automatic calls are off. Works for orders that are New, Call Not Received, Ask Advance, Ship Later or Waiting.
                You can also use the 📞 button on the Order List.
            </p>
            <div className={styles.row} style={{ marginTop: 10 }}>
                <div style={{ maxWidth: 220, flex: '1 1 180px' }}>
                    <input className={styles.input} placeholder="Invoice number" value={invoice} onChange={(e) => setInvoice(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()} aria-label="Invoice number" />
                </div>
                <button className={styles.btnPrimary} onClick={go} disabled={busy || !hasNumber}>{busy ? 'Queueing…' : 'Call customer'}</button>
            </div>
        </div>
    );
}

function AgentSettings({ section, hasNumber, balance, status, onShowMissed }) {
    const [agent, setAgent] = useState(null);
    const [voices, setVoices] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await myAxios.get('call/agent');
                setAgent(res.data.agent);
                setVoices(res.data.voices || []);
            } catch (err) {
                toast.error(errMsg(err, 'Could not load the AI agent'));
            }
        })();
    }, []);

    if (!agent) return <div className={styles.card}><div className={styles.empty}>Loading…</div></div>;

    const set = (patch) => setAgent((a) => ({ ...a, ...patch }));
    const setOC = (patch) => setAgent((a) => ({ ...a, orderConfirm: { ...a.orderConfirm, ...patch } }));
    const setSP = (patch) => setAgent((a) => ({ ...a, support: { ...a.support, ...patch } }));

    const save = async () => {
        try {
            setSaving(true);
            const res = await myAxios.put('call/agent', agent);
            setAgent(res.data.agent);
            toast.success('Saved');
        } catch (err) {
            toast.error(errMsg(err, 'Could not save'));
        } finally {
            setSaving(false);
        }
    };

    const saveBar = (
        <div className={styles.row} style={{ justifyContent: 'flex-end', marginTop: 12 }}>
            <button className={styles.btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
    );
    const noNumber = !hasNumber && <div className={styles.warn}>You have no active number yet. You can set this up now; calls start once our team gives you a number.</div>;

    if (section === 'shop') {
        return (
            <>
                {noNumber}
                <div className={styles.card}>
                    <h2 className={styles.cardTitle} style={{ marginBottom: 6 }}>Shop &amp; voice</h2>
                    <p className={styles.hint} style={{ marginTop: 0, marginBottom: 14 }}>Used by every AI call: order confirmation, customer support and marketing.</p>
                    <div className={styles.formGrid}>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>Shop name (said on every call)</span>
                            <input className={styles.input} maxLength={60} value={agent.storeName || ''} onChange={(e) => set({ storeName: e.target.value })} />
                        </div>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>Voice</span>
                            <select className={styles.select} value={agent.voice || 'Priya'} onChange={(e) => set({ voice: e.target.value })}>
                                {voices.map((v) => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>Agent's name (optional)</span>
                            <input className={styles.input} maxLength={30} value={agent.agentName || ''} onChange={(e) => set({ agentName: e.target.value })} placeholder="e.g. Mim" />
                        </div>
                    </div>
                    <div className={styles.field} style={{ marginTop: 12 }}>
                        <span className={styles.fieldLabel}>Shop info — the AI answers only from this (delivery charge and time, payment, exchange and return rules)</span>
                        <textarea className={styles.textarea} rows={7} maxLength={INFO_MAX} value={agent.storeInfo || ''} onChange={(e) => set({ storeInfo: e.target.value })} placeholder={INFO_EXAMPLE} />
                        <span className={styles.muted} style={{ textAlign: 'right' }}>{(agent.storeInfo || '').length}/{INFO_MAX}</span>
                    </div>
                    {saveBar}
                </div>
            </>
        );
    }

    if (section === 'support') {
        const sp = agent.support || { mode: 'ai' };
        return (
            <>
                {noNumber}
                <div className={styles.card}>
                    <h2 className={styles.cardTitle} style={{ marginBottom: 6 }}>Who answers calls to your number</h2>
                    <div className={styles.choiceGrid}>
                        {MODES.map(([k, title, text]) => (
                            <label key={k} className={`${styles.choiceCard} ${sp.mode === k ? styles.choiceCardActive : ''}`}>
                                <input type="radio" name="supportMode" checked={sp.mode === k} onChange={() => setSP({ mode: k })} />
                                <span className={styles.choiceTitle}>{title}</span>
                                <span className={styles.choiceText}>{text}</span>
                            </label>
                        ))}
                    </div>
                    {(sp.mode === 'softphone' || sp.mode === 'ai_then_human') && (
                        <div className={styles.formGrid} style={{ marginTop: 14 }}>
                            <div className={styles.field}>
                                <span className={styles.fieldLabel}>Ring the softphone for (seconds)</span>
                                <input className={styles.input} type="number" min="10" max="120" value={sp.ringSeconds ?? 30} onChange={(e) => setSP({ ringSeconds: e.target.value })} />
                            </div>
                            <div className={styles.field}>
                                <span className={styles.fieldLabel}>Softphone now</span>
                                <div className={styles.muted} style={{ paddingTop: 8 }}>
                                    {status?.softphoneOnline > 0 ? `${status.softphoneOnline} open (you or your team)` : 'Nobody has it open right now'}
                                </div>
                            </div>
                        </div>
                    )}
                    {sp.mode !== 'off' && sp.mode !== 'softphone' && (
                        <div className={styles.field} style={{ marginTop: 14 }}>
                            <span className={styles.fieldLabel}>Your instructions for the support AI (optional)</span>
                            <textarea className={styles.textarea} rows={4} maxLength={3000} value={sp.prompt || ''} onChange={(e) => setSP({ prompt: e.target.value })} placeholder={SUPPORT_EXAMPLE} />
                        </div>
                    )}
                    <p className={styles.hint}>
                        The AI already knows the caller's recent orders (by their number) and your shop info. Messages it takes appear in Notifications and in Calls.{' '}
                        {onShowMissed && <button className={styles.btnLink} onClick={onShowMissed}>See missed calls</button>}
                    </p>
                    {saveBar}
                </div>
                {sp.mode !== 'off' && sp.mode !== 'softphone' && <TestCall kind="support" hasNumber={hasNumber} balance={balance} label="Test the support AI" />}
            </>
        );
    }

    // section === 'orders'
    const oc = agent.orderConfirm || {};
    return (
        <>
            {noNumber}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Automatic order confirmation</h2>
                    <label className={styles.switchRow}>
                        <input type="checkbox" checked={!!oc.enabled} onChange={(e) => setOC({ enabled: e.target.checked })} />
                        <span>{oc.enabled ? 'On' : 'Off'}</span>
                    </label>
                </div>
                <p className={styles.hint} style={{ marginTop: 0 }}>
                    When on, the AI calls every new order's customer, checks the items, total and address, and marks the order:
                    confirmed → <b>Call Done</b>, cancelled → <b>Cancel</b>, wants it later → <b>Ship Later</b>, wants a change → <b>Waiting</b> (with a note).
                    Customers who never answer: you choose below — the order stays <b>New</b> marked “AI couldn't reach”, or goes to <b>Call Not Received</b>. Only orders placed after you turn it on are called.
                </p>
                <div className={styles.formGrid} style={{ marginTop: 14 }}>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Call after the order (minutes)</span>
                        <input className={styles.input} type="number" min="0" max="1440" value={oc.delayMinutes ?? 5} onChange={(e) => setOC({ delayMinutes: e.target.value })} />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Call hours (Bangladesh time)</span>
                        <div className={styles.row} style={{ flexWrap: 'nowrap' }}>
                            <input className={styles.input} type="time" value={oc.windowStart || '09:00'} onChange={(e) => setOC({ windowStart: e.target.value })} aria-label="From" />
                            <span className={styles.muted}>to</span>
                            <input className={styles.input} type="time" value={oc.windowEnd || '21:00'} onChange={(e) => setOC({ windowEnd: e.target.value })} aria-label="To" />
                        </div>
                    </div>
                </div>
                {(() => {
                    // unanswered / busy: calls a day, days, minutes between (2026-11-04)
                    const rt = { perDay: 2, days: 1, gapMinutes: 120, ...(oc.retry || {}) };
                    const setRT = (patch) => setOC({ retry: { ...rt, ...patch } });
                    const total = Number(rt.perDay) * Number(rt.days);
                    const gapText = (m) => (m < 60 ? `${m} min` : m % 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m / 60} hour${m === 60 ? '' : 's'}`);
                    return (
                        <>
                            <div className={styles.fieldLabel} style={{ marginTop: 16 }}>If the customer doesn't answer</div>
                            <div className={styles.formGrid} style={{ marginTop: 6 }}>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Calls per day</span>
                                    <select className={styles.input} value={rt.perDay} onChange={(e) => setRT({ perDay: Number(e.target.value) })}>
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <option key={n} value={n}>{n} {n === 1 ? 'call' : 'calls'} a day</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Keep trying for</span>
                                    <select className={styles.input} value={rt.days} onChange={(e) => setRT({ days: Number(e.target.value) })}>
                                        {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                                            <option key={n} value={n}>{n} {n === 1 ? 'day' : 'days'}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Gap between calls</span>
                                    <select className={styles.input} value={rt.gapMinutes} onChange={(e) => setRT({ gapMinutes: Number(e.target.value) })} disabled={Number(rt.perDay) === 1}>
                                        {[15, 30, 45, 60, 90, 120, 180, 240, 360].map((m) => (
                                            <option key={m} value={m}>{gapText(m)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className={styles.formGrid} style={{ marginTop: 10 }}>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>After the last call</span>
                                    <select className={styles.input} value={oc.afterLastTry === 'not_received' ? 'not_received' : 'keep_new'} onChange={(e) => setOC({ afterLastTry: e.target.value })}>
                                        <option value="keep_new">Keep the order New (marked “AI couldn't reach”)</option>
                                        <option value="not_received">Move the order to Call Not Received</option>
                                    </select>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>SMS to the customer</span>
                                    <label className={styles.switchRow} style={{ marginTop: 6 }}>
                                        <input type="checkbox" checked={oc.smsAfterLastTry === true} onChange={(e) => setOC({ smsAfterLastTry: e.target.checked })} />
                                        <span>SMS the customer when the AI can't reach them — at most 1 a day, not on every retry (৳0.40 per 160 characters, from your Call balance)</span>
                                    </label>
                                </div>
                            </div>
                            <p className={styles.hint} style={{ marginTop: 6 }}>
                                Busy line → tried again in 15 minutes; phone off → at least an hour later. Each new day starts at a different time
                                (call-hours start, then mid-day). If the customer calls you back, the retries stop. Every try is noted on the order.
                            </p>
                            <p className={styles.hint} style={{ marginTop: 6 }}>
                                Up to <b>{total}</b> {total === 1 ? 'call' : 'calls'} per order: {rt.perDay} a day
                                {Number(rt.perDay) > 1 ? `, ${gapText(Number(rt.gapMinutes))} apart` : ''}, for {rt.days} {Number(rt.days) === 1 ? 'day' : 'days'}. A new day starts at your call hours.
                            </p>
                        </>
                    );
                })()}
                <div className={styles.field} style={{ marginTop: 12 }}>
                    <span className={styles.fieldLabel}>Your instructions for these calls (optional)</span>
                    <textarea className={styles.textarea} rows={4} maxLength={3000} value={oc.prompt || ''} onChange={(e) => setOC({ prompt: e.target.value })} placeholder={ORDER_EXAMPLE} />
                </div>
                <p className={styles.hint}>Orders that come in outside the call hours are called when the hours start. No calls 10 pm – 8 am.</p>
                {saveBar}
            </div>
            <CallOrderNow hasNumber={hasNumber} />
            <TestCall kind="order" hasNumber={hasNumber} balance={balance} label="Test the order confirmation AI" />
        </>
    );
}

export default AgentSettings;
